import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, User, ArrowRight, Loader2, Eye, EyeOff, Sparkles, Download } from "lucide-react";
import axios from "axios";
import { useBranding } from "../hooks/useBranding";
import { resolveBrand, onColor, darken, lighten } from "../lib/brandTheme";
import TwoFactorStep from "../components/security/TwoFactorStep";
import { useTranslation } from "react-i18next";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-portal-252/artifacts/ymujr84m_Logo%20blitzz%20%2B%20BYB%20ok%20sans%20fond%20copie.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login, register, loginWithGoogle, verify2fa } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const tenantParam = searchParams.get("t");
  const { slug } = useParams();
  const [societeBrand, setSocieteBrand] = useState(null);
  const [slugMissing, setSlugMissing] = useState(false);

  // Branding from tenant slug (?t=acme or acme.domain)
  const { brand } = useBranding(tenantParam || undefined);

  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [customLogo, setCustomLogo] = useState("");

  // Registration status from backend
  const [regEnabled, setRegEnabled] = useState(false);
  const [regChecked, setRegChecked] = useState(false);

  // Invite link info
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (!slug) { setSocieteBrand(null); setSlugMissing(false); return; }
    axios.get(`${API}/public/login-brand/${encodeURIComponent(slug)}`).then(r => {
      setSocieteBrand({ ...resolveBrand({ builder: { branding: r.data.branding }, name: r.data.name }), societe_id: r.data.societe_id, slug: r.data.slug });
      setSlugMissing(false);
    }).catch(() => { setSocieteBrand(null); setSlugMissing(true); });
  }, [slug]);

  useEffect(() => {
    // Check registration status
    axios.get(`${API}/auth/registration-status`).then(r => {
      setRegEnabled(r.data.registration_enabled);
      setRegChecked(true);
    }).catch(() => setRegChecked(true));

    // Fetch Blitzz platform logo (fallback)
    axios.get(`${API}/public/logo`).then(r => {
      if (r.data?.company_logo) setCustomLogo(r.data.company_logo);
    }).catch(() => {});

    // Check invite token if present
    if (inviteToken) {
      axios.get(`${API}/auth/invite/${inviteToken}`).then(r => {
        setInviteValid(true);
        setInviteInfo(r.data);
        setIsRegister(true);
        if (r.data.email) setEmail(r.data.email);
        if (r.data.name) setName(r.data.name);
      }).catch(() => {
        setInviteValid(false);
        setError("Ce lien d'invitation est invalide ou a expire.");
      });
    }
  }, [inviteToken]);

  const canShowRegister = regEnabled || inviteValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        if (inviteToken && inviteValid) {
          // Register via invite link
          const res = await axios.post(`${API}/auth/register-invite`, {
            invite_token: inviteToken, email, password, name,
          });
          localStorage.setItem("blitzz_token", res.data.token);
          window.location.href = "/dashboard";
          return;
        }
        await register(email, password, name);
        navigate("/dashboard");
      } else {
        // Page de connexion d'une société : elle devient la société active (si l'utilisateur y a accès)
        if (societeBrand?.societe_id) localStorage.setItem("blitzz_societe_id", societeBrand.societe_id);
        const u = await login(email, password);
        if (u?.requires_2fa) { setChallenge(u); setLoading(false); return; }
        const dest = (u?.role === "eleve" || u?.role === "stagiaire")
          ? "/mes-formations"
          : u?.role === "partner" ? "/partner"
          : u?.role === "tenant_member" ? "/mon-espace" : "/dashboard";
        navigate(dest);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    }
    setLoading(false);
  };

  const afterLogin = (u) => {
    const dest = (u?.role === "eleve" || u?.role === "stagiaire") ? "/mes-formations"
      : u?.role === "partner" ? "/partner" : u?.role === "tenant_member" ? "/mon-espace" : "/dashboard";
    navigate(dest);
  };

  // Derive brand values with fallbacks (société /login/<slug> > marque blanche tenant > Blitzz)
  const sb = societeBrand;
  const light = sb?.theme === "light";
  const whiteLabel = !sb && !!(brand && brand.whitelabel_enabled);
  const logoSrc = (sb && ((light && sb.logoLight) || sb.logo)) || (whiteLabel && brand?.brand_logo) || customLogo || LOGO_URL;
  const primary = sb?.primary || (whiteLabel && brand?.brand_color_primary) || "#f7c935";
  const secondary = sb?.secondary || (whiteLabel && brand?.brand_color_secondary) || "#461d8a";
  const title = sb?.title || (whiteLabel && brand?.brand_login_title) || t("auth.welcome");
  const subtitle = sb?.tagline || (whiteLabel && brand?.brand_login_subtitle) || t("auth.subtitle");
  const heroImg = sb ? sb.cover : (whiteLabel ? brand?.brand_login_hero_image : "");
  const heroBg = sb ? sb.background : "#0b0811";
  const surface = sb ? (light ? "#ffffff" : lighten(sb.background, 0.05)) : "#14101c";
  const inputBg = sb ? (light ? "#f3f4f6" : darken(sb.background, 0.25)) : "#0b0811";
  const fg = light ? "#202020" : "#ffffff";
  const muted = light ? "#5b6472" : "#94a3b8";
  const onPrimary = onColor(primary);
  const brandName = sb?.name || (whiteLabel && brand?.tenant_name) || "";
  const fontFamily = sb?.font ? `${sb.font}, ui-sans-serif, system-ui, sans-serif` : undefined;

  return (
    <div className="min-h-screen flex" data-testid="login-page" data-societe-slug={sb?.slug || ""} style={{ color: fg, fontFamily }}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          background: heroImg
            ? `url(${heroImg}) center/cover no-repeat`
            : `radial-gradient(circle at 30% 40%, ${secondary} 0%, ${heroBg} 70%)`,
        }}>
        {heroImg && <div className="absolute inset-0" style={{ background: sb ? `linear-gradient(90deg, ${heroBg}cc 0%, ${heroBg}55 60%, transparent 100%)` : "rgba(0,0,0,0.4)" }} />}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"40\" height=\"40\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cpath d=\"M0 0h40v40H0z\" fill=\"none\"%2F%3E%3Cpath d=\"M20 0L40 20L20 40L0 20z\" fill=\"none\" stroke=\"" + encodeURIComponent(primary) + "\" stroke-width=\"0.5\"%2F%3E%3C%2Fsvg%3E')" }} />
        <div className="relative z-10 px-16 max-w-lg">
          <img src={sb?.logo || logoSrc} alt={brandName || "Blitzz"} className="w-96 mb-12 object-contain" data-testid="login-branding-logo" />
          <h1 className="text-5xl font-black tracking-tighter text-white leading-none mb-6">
            {title.split(" ").slice(0, -1).join(" ")}{" "}
            <span style={{ color: primary }}>{title.split(" ").slice(-1).join(" ")}</span>
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">{subtitle}</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 relative" style={{ background: surface }}>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-10">
            <img src={logoSrc} alt={brandName || "Blitzz"} className="h-16 object-contain" data-testid="login-mobile-logo" />
          </div>
          {slugMissing && (
            <div className="mb-6 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 text-sm" data-testid="login-slug-missing">
              Aucune société ne correspond à cette adresse de connexion. Vous êtes sur la page de connexion par défaut.
            </div>
          )}

          {inviteToken && inviteValid && (
            <div className="mb-6 p-3 rounded-lg border border-green-500/30 bg-green-500/10 text-green-400 text-sm" data-testid="invite-banner">
              Vous avez ete invite a rejoindre la plateforme. Creez votre compte ci-dessous.
            </div>
          )}
          {brandName && (
            <div className="mb-4 text-[10px] uppercase tracking-widest font-bold" style={{ color: primary }} data-testid="branded-tenant-badge">
              {brandName}
            </div>
          )}

          {challenge ? (
            <TwoFactorStep challenge={challenge} onBack={() => setChallenge(null)} primary={primary} onPrimary={onPrimary} light={light} inputBg={inputBg} muted={muted}
              onVerify={async (code, remember) => { const u = await verify2fa(challenge.challenge_id, code, remember); afterLogin(u); }} />
          ) : (<>
          <h2 className="text-3xl font-black tracking-tight mb-2">
            {isRegister ? t("auth.register") : t("auth.login")}
          </h2>
          <p className="mb-8" style={{ color: muted }}>
            {isRegister ? t("auth.register_hint") : (sb ? `Accédez à votre espace ${sb.name}` : t("auth.login_hint"))}
          </p>

          {error && (
            <div className="mb-6 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <Label className="text-sm font-medium mb-2 block" style={{ color: light ? "#374151" : "#cbd5e1" }}>{t("auth.full_name")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Votre nom"
                    className={`pl-10 h-12 ${light ? "border-black/10 text-[#202020] placeholder:text-slate-400" : "border-white/10 text-white placeholder:text-slate-500"}`} style={{ background: inputBg }}
                    required
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: light ? "#374151" : "#cbd5e1" }}>{t("auth.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@entreprise.fr"
                  className={`pl-10 h-12 ${light ? "border-black/10 text-[#202020] placeholder:text-slate-400" : "border-white/10 text-white placeholder:text-slate-500"}`} style={{ background: inputBg }}
                  required
                  readOnly={inviteToken && inviteValid && inviteInfo?.email}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: light ? "#374151" : "#cbd5e1" }}>{t("auth.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`pl-10 pr-10 h-12 ${light ? "border-black/10 text-[#202020] placeholder:text-slate-400" : "border-white/10 text-white placeholder:text-slate-500"}`} style={{ background: inputBg }}
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 font-bold text-base rounded-lg transition-colors"
              style={{ background: primary, color: onPrimary }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? t("common.loading") : (isRegister ? t("auth.create_account") : t("auth.submit"))}
              {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${light ? "border-black/10" : "border-white/10"}`} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-sm" style={{ background: surface, color: muted }}>{t("auth.or")}</span>
            </div>
          </div>

          <Button
            data-testid="google-login-btn"
            variant="outline"
            onClick={loginWithGoogle}
            className={`w-full h-12 bg-transparent font-semibold rounded-lg transition-colors ${light ? "border-black/15 text-[#202020] hover:bg-black/5" : "border-white/10 text-white hover:bg-white/5"}`}
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t("auth.continue_google")}
          </Button>

          {canShowRegister && (
            <p className="mt-8 text-center text-sm text-slate-400" data-testid="toggle-auth-area">
              {isRegister ? t("auth.have_account") : t("auth.no_account")}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => { setIsRegister(!isRegister); setError(""); }}
                className="ml-1 font-semibold hover:underline"
                style={{ color: primary }}
              >
                {isRegister ? t("auth.submit") : t("auth.create_account")}
              </button>
            </p>
          )}
          {!regChecked && <span className="sr-only">checking</span>}
          </>)}
          <p className="mt-6 text-center text-xs" style={{ color: muted }}>
            <a href="/telechargements" className="hover:underline inline-flex items-center gap-1" style={{ color: primary }} data-testid="login-downloads-link"><Download className="w-3 h-3" /> Télécharger les applications (Formation, Salarié, Partenaire)</a>
          </p>
        </div>
        {sb && (sb.legal || sb.footer) && (
          <footer className="w-full max-w-md mt-10 lg:absolute lg:bottom-4 lg:left-8 lg:right-8 lg:w-auto lg:max-w-none lg:mt-0" data-testid="login-legal-footer">
            {sb.footer && <div className="text-xs font-semibold mb-1" style={{ color: primary }}>{sb.footer}</div>}
            {sb.legal && <p className="text-[10px] leading-relaxed" style={{ color: muted }}>{sb.legal}</p>}
          </footer>
        )}
      </div>
    </div>
  );
}
